import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authApi } from '../api/client';
import { useAuthStore } from '../store';
import { Image } from '../components/common/NextAdapters';
import styles from './LoginPage.module.css';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';
import { FiEye, FiEyeOff, FiMail, FiLock, FiUser, FiAtSign, FiCheckCircle } from 'react-icons/fi';

export default function LoginPage() {
  usePageTitle('Sign In · Kast');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuthStore();

  // A redirect from the global session-expiry handler (see api/client.js) carries where the
  // user was and why, so they land back where they left off instead of just the homepage.
  const redirectTo = searchParams.get('redirect');
  const sessionExpired = searchParams.get('sessionExpired') === '1';

  useEffect(() => {
    if (sessionExpired) {
      toast.error('Your session expired — please sign in again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [identifier, setIdentifier] = useState(''); // email or username (login)
  const [email, setEmail] = useState('');           // register only
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  // Shown after a successful registration, or when login is blocked pending activation.
  const [pendingActivationEmail, setPendingActivationEmail] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  const resetFields = () => {
    setFullName(''); setUsername(''); setIdentifier(''); setEmail(''); setPassword('');
  };

  const handleResendActivation = async () => {
    if (!pendingActivationEmail) return;
    setResendLoading(true);
    try {
      await authApi.resendActivation(pendingActivationEmail);
      toast.success('Activation email sent — check your inbox.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not resend the activation email.'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'login' && !identifier) { toast.error('Please enter your email or username'); return; }
    if (mode === 'register' && !email) { toast.error('Please enter your email'); return; }
    if (mode === 'register' && !username.trim()) { toast.error('Please choose a username'); return; }
    if (!password) { toast.error('Please enter your password'); return; }
    if (mode === 'register' && !fullName.trim()) { toast.error('Please enter your full name'); return; }

    // Username validation: alphanumeric + underscore only
    if (mode === 'register' && !/^[a-zA-Z0-9_]{3,20}$/.test(username.trim())) {
      toast.error('Username must be 3–20 characters: letters, numbers, underscores only');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const response = await authApi.login(identifier, password);
        const { data } = response;

        if (data.success && data.data?.Token) {
          const { Token, ...userFields } = data.data;
          setAuth(Token, userFields);
          toast.success('Welcome back!');

          // Prefer sending them back where they were (e.g. after a session-expiry redirect)
          // over the "Who's watching?" profile picker — but only ever to a same-site relative path.
          if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')) {
            navigate(redirectTo);
            return;
          }

          // Netflix-style: always land on the profile picker after signing in.
          // WhosWatchingPage handles the role-based redirect (Admin/ContentCreator/User)
          // once a profile is chosen.
          navigate('/whos-watching');
        } else {
          toast.error(data.message || 'Authentication failed');
        }
      } else {
        const response = await authApi.register(fullName.trim(), username.trim(), email, password, 'User');
        const { data } = response;

        if (data.success) {
          // Accounts now require email activation before they can sign in.
          setPendingActivationEmail(email);
          toast.success('Account created! Check your email to activate it.');
        } else {
          toast.error(data.message || 'Registration failed');
        }
      }
    } catch (err) {
      // A login blocked on activation gets a friendlier, actionable message.
      if (err?.response?.data?.errorCode === 'EMAIL_NOT_CONFIRMED') {
        setPendingActivationEmail(identifier.includes('@') ? identifier : null);
        toast.error('Please activate your account first — check your email.');
      } else {
        toast.error(getApiErrorMessage(err, 'Request failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'var(--popover)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '10px',
            fontSize: '14px',
            backdropFilter: 'blur(12px)',
          },
          success: { iconTheme: { primary: 'var(--primary)', secondary: 'var(--primary-foreground)' } },
          error: { iconTheme: { primary: 'var(--destructive)', secondary: '#fff' } },
          duration: 4000,
        }}
      />
      <div className={styles.bg}>
        <Image src="/bg.png" alt="" fill priority className={styles.bgImg} />
        <div className={styles.bgOverlay} />
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </div>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Logo */}
        <Link to="/" className={styles.logo} aria-label="Go to home">
          <img
            src="/cast-logo-full.png"
            alt="BoldscholarCast"
            className={styles.logoImg}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className={styles.logoFallback}></div>
        </Link>

        {pendingActivationEmail ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <FiCheckCircle size={40} color="var(--primary)" />
            </div>
            <h1 className={styles.heading}>Check Your Email</h1>
            <p className={styles.sub}>
              We sent an activation link to <strong>{pendingActivationEmail}</strong>. Click it to activate
              your account, then sign in below.
            </p>
            <button
              type="button"
              className={styles.submitBtn}
              disabled={resendLoading}
              onClick={handleResendActivation}
            >
              {resendLoading ? <span className={styles.spinner} /> : 'Resend Activation Email'}
            </button>
            <div className={styles.divider}><span>or</span></div>
            <button
              type="button"
              className={styles.browseLink}
              style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer' }}
              onClick={() => { setPendingActivationEmail(null); setMode('login'); resetFields(); }}
            >
              Back to Sign In
            </button>
          </>
        ) : (
          <>
            {/* Mode toggle */}
            <div className={styles.modeToggle}>
              <button
                className={`${styles.modeBtn} ${mode === 'login' ? styles.modeBtnActive : ''}`}
                onClick={() => { setMode('login'); resetFields(); }}
              >Sign In</button>
              <button
                className={`${styles.modeBtn} ${mode === 'register' ? styles.modeBtnActive : ''}`}
                onClick={() => { setMode('register'); resetFields(); }}
              >Create Account</button>
            </div>

            <h1 className={styles.heading}>
              {mode === 'login' ? 'Welcome Back' : 'Get Started'}
            </h1>
            <p className={styles.sub}>
              {mode === 'login' ? 'Sign in to your account' : 'Create your free account'}
            </p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              {/* Full name — register only */}
              {mode === 'register' && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="fullName">Full Name</label>
                  <div className={styles.inputWrap}>
                    <FiUser className={styles.inputIcon} />
                    <input
                      id="fullName"
                      type="text"
                      className={styles.input}
                      placeholder="Your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      autoComplete="name"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Username — register only */}
              {mode === 'register' && (
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="username">Username</label>
                  <div className={styles.inputWrap}>
                    <FiAtSign className={styles.inputIcon} />
                    <input
                      id="username"
                      type="text"
                      className={styles.input}
                      placeholder="e.g. john_doe (3–20 chars)"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      autoComplete="username"
                      maxLength={20}
                      required
                    />
                  </div>
                  <span className={styles.fieldHint}>Letters, numbers and underscores only</span>
                </div>
              )}

              {/* Email / Username (login) */}
              <div className={styles.field}>
                <label className={styles.label} htmlFor="identifier">
                  {mode === 'login' ? 'Email or Username' : 'Email'}
                </label>
                <div className={styles.inputWrap}>
                  <FiMail className={styles.inputIcon} />
                  {mode === 'login' ? (
                    <input
                      id="identifier"
                      type="text"
                      className={styles.input}
                      placeholder="email@example.com or username"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      autoComplete="username"
                      required
                    />
                  ) : (
                    <input
                      id="email"
                      type="email"
                      className={styles.input}
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                    />
                  )}
                </div>
              </div>

              {/* Password */}
              <div className={styles.field}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className={styles.label} htmlFor="password">Password</label>
                  {mode === 'login' && (
                    <Link to="/forgot-password" className={styles.browseLink} style={{ fontSize: 12, display: 'inline' }}>
                      Forgot password?
                    </Link>
                  )}
                </div>
                <div className={styles.inputWrap}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    className={styles.input}
                    placeholder={mode === 'register' ? 'Min. 6 characters' : 'Your password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <motion.button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? <span className={styles.spinner} /> : mode === 'login' ? 'Sign In' : 'Create Account'}
              </motion.button>
            </form>

            <div className={styles.divider}><span>or</span></div>

            <Link to="/" className={styles.browseLink}>
              Browse without signing in →
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
